import { BaseApiService, ApiResponse } from './baseApi';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';

// Work Order Variant Breakdown interfaces
export interface WorkOrderVariantBreakdown {
  id: number;
  quantity: number;
  completedQuantity: number;
  rejectedQuantity: number;
  materialIssued: number;
  isActive: boolean;
  createdBy?: number;
  updatedBy?: number;
  createdAt: string;
  updatedAt: string;
  workOrderId: number;
  endgoodId: number;
  endgoodVariantId: number;
  remainingQuantity: number;
  completionPercentage: number;
  rejectionPercentage: number;
  isFullyCompleted: boolean;
  isPartiallyCompleted: boolean;
  hasRejections: boolean;
  workOrderNumber: string;
  endgoodName: string;
  endgoodCode: string;
  endgoodVariantColor: string;
  customerName: string;
  department: string;
}

export interface WorkOrderVariantBreakdownCreateRequest {
  quantity: number;
  completedQuantity: number;
  rejectedQuantity: number;
  materialIssued: number;
  isActive: boolean;
  workOrderId: number;
  endgoodId: number;
  endgoodVariantId: number;
}


export interface WorkOrderVariantBreakdownResponse {
  success: boolean;
  message: string;
  data?: WorkOrderVariantBreakdown[];
  error?: string;
}

export interface WorkOrderVariantBreakdownBulkResponse {
  success: boolean;
  message: string;
  data?: WorkOrderVariantBreakdown[];
  error?: string;
}

class WorkOrderVariantBreakdownApiService extends BaseApiService {
  // Create multiple work order variant breakdowns
  async createBulkWorkOrderVariantBreakdowns(
    breakdowns: WorkOrderVariantBreakdownCreateRequest[]
  ): Promise<WorkOrderVariantBreakdownBulkResponse> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderVariantBreakdown[]>>(
        API_ENDPOINTS.WORK_ORDER_VARIANT_BREAKDOWNS.CREATE_BULK,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(breakdowns)
        }
      );

      return {
        success: true,
        message: 'Work order variant breakdowns created successfully',
        data: response.data
      };
    } catch (error: any) {
      // console.error('Error creating work order variant breakdowns:', error);
      return {
        success: false,
        message: 'Failed to create work order variant breakdowns',
        error: error.response?.data?.message || error.message
      };
    }
  }

  // Get work order variant breakdowns by work order ID
  async getWorkOrderVariantBreakdownsByWorkOrder(
    workOrderId: number
  ): Promise<WorkOrderVariantBreakdownResponse> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderVariantBreakdown[]>>(
        API_ENDPOINTS.WORK_ORDER_VARIANT_BREAKDOWNS.BY_WORK_ORDER(workOrderId.toString()),
        {
          method: HTTP_METHODS.GET
        }
      );

      return {
        success: true,
        message: 'Work order variant breakdowns retrieved successfully',
        data: response.data
      };
    } catch (error: any) {
      // console.error('Error getting work order variant breakdowns:', error);
      return {
        success: false,
        message: 'Failed to get work order variant breakdowns',
        error: error.response?.data?.message || error.message
      };
    }
  }

  // Get work order variant breakdowns by endgood variant ID
  async getWorkOrderVariantBreakdownsByEndgoodVariant(
    endgoodVariantId: number
  ): Promise<WorkOrderVariantBreakdownResponse> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderVariantBreakdown[]>>(
        API_ENDPOINTS.WORK_ORDER_VARIANT_BREAKDOWNS.BY_ENDGOOD_VARIANT(endgoodVariantId.toString()),
        {
          method: HTTP_METHODS.GET
        }
      );

      return {
        success: true,
        message: 'Work order variant breakdowns retrieved successfully',
        data: response.data
      };
    } catch (error: any) {
      // console.error('Error getting work order variant breakdowns:', error);
      return {
        success: false,
        message: 'Failed to get work order variant breakdowns',
        error: error.response?.data?.message || error.message
      };
    }
  }

  // Get work order variant breakdowns by endgood ID
  async getWorkOrderVariantBreakdownsByEndgood(
    endgoodId: number
  ): Promise<WorkOrderVariantBreakdownResponse> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderVariantBreakdown[]>>(
        API_ENDPOINTS.WORK_ORDER_VARIANT_BREAKDOWNS.BY_ENDGOOD(endgoodId.toString()),
        {
          method: HTTP_METHODS.GET
        }
      );

      return {
        success: true,
        message: 'Work order variant breakdowns retrieved successfully',
        data: response.data
      };
    } catch (error: any) {
      // console.error('Error getting work order variant breakdowns by endgood:', error);
      return {
        success: false,
        message: 'Failed to get work order variant breakdowns by endgood',
        error: error.response?.data?.message || error.message
      };
    }
  }

  // Update work order variant breakdown
  async updateWorkOrderVariantBreakdown(
    id: number,
    breakdown: Partial<WorkOrderVariantBreakdownCreateRequest>
  ): Promise<WorkOrderVariantBreakdownResponse> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrderVariantBreakdown>>(
        API_ENDPOINTS.WORK_ORDER_VARIANT_BREAKDOWNS.UPDATE(id.toString()),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(breakdown)
        }
      );

      return {
        success: true,
        message: 'Work order variant breakdown updated successfully',
        data: [response.data]
      };
    } catch (error: any) {
      // console.error('Error updating work order variant breakdown:', error);
      return {
        success: false,
        message: 'Failed to update work order variant breakdown',
        error: error.response?.data?.message || error.message
      };
    }
  }

  // Delete work order variant breakdown
  async deleteWorkOrderVariantBreakdown(id: number): Promise<WorkOrderVariantBreakdownResponse> {
    try {
      await this.makeRequest<void>(
        API_ENDPOINTS.WORK_ORDER_VARIANT_BREAKDOWNS.DELETE(id.toString()),
        {
          method: HTTP_METHODS.DELETE
        }
      );

      return {
        success: true,
        message: 'Work order variant breakdown deleted successfully'
      };
    } catch (error: any) {
      // console.error('Error deleting work order variant breakdown:', error);
      return {
        success: false,
        message: 'Failed to delete work order variant breakdown',
        error: error.response?.data?.message || error.message
      };
    }
  }
}

export const workOrderVariantBreakdownApiService = new WorkOrderVariantBreakdownApiService();
